<template>
    <div>
        <page
            :busy="busy"
            :tabs="tabs"
            :can-add-tab="true"
            :compact-mobile-view="true"
            @add-tab="handleAddTab"
        >
            <template v-if="currentEmbeddedPage">
                <b-container fluid>
                    <b-row class="mt-4">
                        <b-col md="5" lg="3">
                            <b-row>
                                <b-col>
                                    <b-button
                                        v-b-modal="embedCodeModalID"
                                        class="btn-html-code align-baseline mr-1"
                                        variant="primary"
                                    >
                                        Get HTML Code
                                    </b-button>
                                    <b-button
                                        class="align-baseline"
                                        variant="primary"
                                        title="Remove embedded page"
                                        @click="handleRemoveTab"
                                    >
                                        <font-awesome-icon
                                            :icon="['fas', 'trash']"
                                        ></font-awesome-icon>
                                    </b-button>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col class="mt-4">
                                    <form-input
                                        class="embedded-page-title-input"
                                        v-model="currentEmbeddedPage.name"
                                        @update="() => handleUpdateTabTitle()"
                                    ></form-input>
                                </b-col>
                            </b-row>
                            <b-row class="mt-4">
                                <b-col>
                                    <embedded-page-settings-form
                                        :embedded-page="currentEmbeddedPage"
                                        @success="handleEmbeddedPageUpdateSuccess"
                                    ></embedded-page-settings-form>
                                </b-col>
                            </b-row>
                            <b-row class="mt-4">
                                <b-col>
                                    <feature-list
                                        :embedded-page="currentEmbeddedPage"
                                        @update-success="handleFeatureUpdateSuccess"
                                    ></feature-list>
                                </b-col>
                            </b-row>
                            <b-row class="mt-4">
                                <b-col>
                                    <properties-area-list
                                        v-if="propertiesAreas.length > 0"
                                        :embedded-page="currentEmbeddedPage"
                                        :properties-areas="propertiesAreas"
                                        @property-area-update-success="handlePropertyAreaUpdateSuccess"
                                    ></properties-area-list>
                                </b-col>
                            </b-row>
                            <b-row class="mt-4">
                                <b-col>
                                    <tab-list
                                        :embedded-page="currentEmbeddedPage"
                                        @update-success="handleTabUpdateSuccess"
                                    ></tab-list>
                                </b-col>
                            </b-row>
                            <b-row class="mt-4">
                                <b-col>
                                    <property-list
                                        v-if="properties.length > 0"
                                        :embedded-page="currentEmbeddedPage"
                                        :properties="properties"
                                        @property-update-success="handlePropertyUpdateSuccess"
                                    ></property-list>
                                </b-col>
                            </b-row>
                        </b-col>
                        <b-col md="7" lg="9">
                            <iframe
                                v-if="currentEmbeddedPageSettingsFrameSrc"
                                :id="currentEmbeddedPageSettingsFrameID"
                                :src="currentEmbeddedPageSettingsFrameSrc"
                                class="embedded-page-settings-frame mb-3"
                                frameborder="0"
                                marginheight="0"
                                marginwidth="0"
                                scrolling="no"
                                @load="iframeLoad"
                            ></iframe>
                            <iframe
                                v-if="currentEmbeddedPageSettingsPropertyFrameSrc"
                                :id="currentEmbeddedPageSettingsPropertyFrameID"
                                :src="currentEmbeddedPageSettingsPropertyFrameSrc"
                                class="embedded-page-settings-property-frame mb-3"
                                frameborder="0"
                                marginheight="0"
                                marginwidth="0"
                                scrolling="no"
                                @load="iframePropertyLoad"
                            ></iframe>
                        </b-col>
                    </b-row>
                </b-container>
                <modal
                    :id="embedCodeModalID"
                    title="Embed Code"
                    ok-title="Copy to clipboard"
                    @ok="copyToClipboard"
                    @shown="handleEmbedCodeModalShown"
                >
                    <b-form-textarea
                        ref="embeddedPageCode"
                        readonly
                        :value="currentEmbeddedPage.code"
                        @focus="$event.target.select()"
                        class="lx-embedded-modal-textarea"
                    ></b-form-textarea>
                </modal>
            </template>
        </page>
        <modal
            :id="removeEmbeddedPageModalID"
            title="Remove Embedded Page"
            @ok="deleteCurrentEmbeddedPageForRemove"
        >Are you sure you want to remove {{ currentEmbeddedPageForRemoveName }}?</modal>
    </div>
</template>

<script>
    import debounce from 'lodash/debounce';
    import { mapGetters } from 'vuex';
    import API from '../../../api';
    import Modal from './../../generic/modal/modal.vue';
    import Page from '../../generic/page/page.vue';
    import EmbeddedPageSettingsForm from './embedded-page-settings-form.vue';
    import FeatureList from './feature-list.vue';
    import PropertiesAreaList from './properies-area-list.vue';
    import PropertyList from './property-list.vue';
    import TabList from './tab-list.vue';
    import FormInput from '../../generic/form-input/form-input.vue';

    export default {
        name: 'EmbeddedPageSettings',
        components: {
            EmbeddedPageSettingsForm,
            Modal,
            Page,
            FeatureList,
            PropertiesAreaList,
            PropertyList,
            TabList,
            FormInput
        },
        computed: {
            ...mapGetters('embeddedPage', ['currentEmbeddedPage', 'embeddedPages', 'embeddedPageByID']),
            currentRouteID() {
                return parseInt(this.$route.query.id);
            },
            currentEmbeddedPageForRemoveName() {
                if (this.currentEmbeddedPageForRemoveID) {
                    const embeddedPage = this.embeddedPageByID(this.currentEmbeddedPageForRemoveID);
                    if (embeddedPage) {
                        return embeddedPage.name;
                    }
                }
                return 'this embedded page';
            },
            currentEmbeddedPageSettingsFrameID() {
                if (this.currentEmbeddedPage) {
                    return `embedded-page-settings-iframe-${this.currentEmbeddedPage.id}`;
                }
                return 'embedded-page-settings-iframe';
            },
            currentEmbeddedPageSettingsFrameSrc() {
                if (this.currentEmbeddedPage) {
                    return `/embed/${this.currentEmbeddedPage.id}/inventory/?total=1&with_redirect=false`;
                }
            },
            currentEmbeddedPageSettingsPropertyFrameID() {
                return `embedded-page-settings-property-iframe`;
            },
            tabs() {
                return this.embeddedPages.map(embeddedPage => ({
                    id: embeddedPage.id,
                    title: embeddedPage.name,
                    route: this.$route.path + `?id=${embeddedPage.id}`,
                    active: !!this.currentEmbeddedPage && this.currentEmbeddedPage.id === embeddedPage.id,
                }))
            }
        },
        data() {
            return {
                busy: false,
                currentEmbeddedPageForRemoveID: null,
                currentEmbeddedPageSettingsPropertyFrameSrc: null,
                removeEmbeddedPageModalID: 'remove-embedded-page-modal',
                embedCodeModalID: 'embed-code-modal',
                properties: [],
                propertiesAreas: [],
            }
        },
        mounted() {
            (async () => {
                this.busy = true;
                try {
                    await Promise.all([
                        this.fetchEmbeddedPages(),
                        this.fetchProperties(),
                        this.fetchPropertiesAreas(),
                    ]);
                } finally {
                    this.busy = false;
                }
            })();
        },
        methods: {
            async fetchEmbeddedPages() {
                try {
                    await this.$store.dispatch('embeddedPage/fetchEmbeddedPages');
                } catch (err) {
                    this.errorToast({text: 'Could not load embedded page data.'})
                }

                if (this.currentRouteID) {
                    this.$store.commit('embeddedPage/assignCurrentEmbeddedPage', { id: this.currentRouteID });
                } else {
                    this.$store.commit('embeddedPage/assignFirstCurrentEmbeddedPage');
                }
            },
            async fetchProperties() {
                try {
                    const response = await API.get('/app/embedded-page/embedded-page/properties/');
                    this.properties = response.data;
                } catch (err) {
                    this.errorToast({text: 'Could not load property data.'})
                }
            },
            async fetchPropertiesAreas() {
                try {
                    const response = await API.get('/app/embedded-page/embedded-page/properties-areas/');
                    this.propertiesAreas = response.data;
                } catch (err) {
                    this.errorToast({text: 'Could not load properties area data.'})
                }
            },
            async createEmbeddedPage() {
                try {
                    await this.$store.dispatch('embeddedPage/createEmbeddedPage');
                } catch (err) {
                    this.errorToast({text: 'Could not create embedded page.'})
                }
            },
            async updateEmbeddedPageName(id, name) {
                try {
                    await this.$store.dispatch('embeddedPage/updateEmbeddedPage', {
                        id,
                        name,
                    });
                } catch (err) {
                    this.errorToast({text: 'Could not update embedded page name.'})
                }
            },
            async deleteEmbeddedPage(id) {
                try {
                    await this.$store.dispatch('embeddedPage/deleteEmbeddedPage', { id });
                } catch (err) {
                    this.errorToast({text: 'Could not delete embedded page.'})
                }
            },
            async deleteCurrentEmbeddedPageForRemove() {
                if (this.currentEmbeddedPageForRemoveID) {
                    await this.deleteEmbeddedPage(this.currentEmbeddedPageForRemoveID);
                    this.currentEmbeddedPageForRemoveID = null;
                }
            },
            async copyToClipboard() {
                try {
                    await navigator.clipboard.writeText(this.currentEmbeddedPage.code);
                    this.successToast({text: 'Copied to clipboard successfully.'})
                    this.$bvModal.hide(this.embedCodeModalID);
                } catch (err) {
                    this.errorToast({text: 'Something went wrong while copying to clipboard.'})
                }
            },
            async handleAddTab() {
                await this.createEmbeddedPage();
            },
            iframePropertyLoad() {
                const iframe = document.getElementById(this.currentEmbeddedPageSettingsPropertyFrameID);
                iframe.style.height = iframe.contentWindow.document.body.offsetHeight + 'px';
            },
            iframePropertyRefresh() {
                const iframe = document.getElementById(this.currentEmbeddedPageSettingsPropertyFrameID);
                iframe.src = iframe.src;
            },
            iframeLoad() {
                const iframe = document.getElementById(this.currentEmbeddedPageSettingsFrameID);
                iframe.style.height = iframe.contentWindow.document.body.offsetHeight + 'px';

                const propertyLinks = iframe.contentWindow.document.getElementsByClassName('ldx-property-title');
                if (propertyLinks.length > 0) {
                    this.currentEmbeddedPageSettingsPropertyFrameSrc = propertyLinks[0].href;
                }
            },
            iframeRefresh() {
                const iframe = document.getElementById(this.currentEmbeddedPageSettingsFrameID);
                iframe.src = iframe.src;
            },
            handlePropertyAreaUpdateSuccess() {
                this.iframeRefresh();
                this.iframePropertyRefresh();
            },
            handlePropertyUpdateSuccess() {
                this.iframeRefresh();
                this.iframePropertyRefresh();
            },
            handleTabUpdateSuccess() {
                this.iframeRefresh();
                this.iframePropertyRefresh();
            },
            handleFeatureUpdateSuccess() {
                this.iframeRefresh();
                this.iframePropertyRefresh();
            },
            handleEmbeddedPageUpdateSuccess() {
                this.iframeRefresh();
                this.iframePropertyRefresh();
            },
            handleEmbedCodeModalShown() {
                this.$nextTick(() => {
                    this.$refs.embeddedPageCode.focus();
                });
            },
            handleUpdateTabTitle: debounce(function () {
                return this.updateEmbeddedPageName(this.currentEmbeddedPage.id, this.currentEmbeddedPage.name)
            }, 500),
            handleRemoveTab() {
                const activeTab = this.tabs.find(tab => tab.active)
                if (activeTab) {
                    this.currentEmbeddedPageForRemoveID = activeTab.id;
                    this.$bvModal.show(this.removeEmbeddedPageModalID);
                }
            }
        },
        watch: {
            currentRouteID(id) {
                this.$store.commit('embeddedPage/assignCurrentEmbeddedPage', { id });
            },
            currentEmbeddedPage() {
                this.$router.push({
                    path: this.$route.path,
                    query: { id: this.currentEmbeddedPage.id }
                }).catch(err => {});
            },

        }
    }
</script>

<style lang="scss" scoped>
    .lx-embedded-modal-textarea {
        min-height: 100px;
    }

    .embedded-page-title-input {
        padding: 0 18px;
    }

    .btn-html-code {
        margin-left: 18px !important;
    }

    .embedded-page-settings-frame {
        height: auto;
        min-height: 600px;
        min-width: 98%;
    }

    .embedded-page-settings-property-frame {
        min-width: 98%;
        height: auto;
    }
</style>
